import { useEffect, useState } from "react";
import { Link } from "wouter";
import { api, type Lead } from "@/lib/api-client";
import { Header } from "@/components/layout/Header";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import { STATUS_COLORS } from "@/lib/constants";
import { cn, formatTimeAgo } from "@/lib/utils";
import { useDashboardRealtime } from "@/lib/useDashboardRealtime";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { RenameChatDialog } from "@/components/chat/RenameChatDialog";
import { Search, Bot, BotOff, ArrowRight, Phone, Clock, Filter, MoreVertical, Pencil } from "lucide-react";

export function ChatsPage() {
  const [chats, setChats] = useState<Lead[]>([]);
  const [filteredChats, setFilteredChats] = useState<Lead[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [aiFilter, setAiFilter] = useState<string>("all");
  const [renameTarget, setRenameTarget] = useState<Lead | null>(null);
  const [showRename, setShowRename] = useState(false);

  useEffect(() => {
    loadChats();
  }, []);

  useDashboardRealtime({
    onLeadMessage: ({ leadId, createdAt }) => {
      setChats((prev) => {
        const idx = prev.findIndex((c) => c.id === leadId);
        if (idx < 0) return prev;
        const next = prev.map((c) => (c.id === leadId ? { ...c, updatedAt: createdAt } : c));
        return [...next].sort((a, b) => b.updatedAt - a.updatedAt);
      });
    },
    onLeadUpdated: (lead) => {
      setChats((prev) => {
        const idx = prev.findIndex((c) => c.id === lead.id);
        if (idx < 0) return prev;
        const next = prev.map((c) => (c.id === lead.id ? { ...c, ...lead } : c));
        return [...next].sort((a, b) => b.updatedAt - a.updatedAt);
      });
    },
  });

  useEffect(() => {
    filterChats();
  }, [chats, search, statusFilter, aiFilter]);

  const loadChats = async () => {
    try {
      const { chats } = await api.getChats({ limit: 100 });
      setChats(chats);
    } catch (error) {
      console.error("Failed to load chats:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const filterChats = () => {
    let result = [...chats];

    // Search filter
    if (search) {
      const searchLower = search.toLowerCase();
      result = result.filter(
        (chat) =>
          chat.name?.toLowerCase().includes(searchLower) ||
          chat.phone.toLowerCase().includes(searchLower)
      );
    }

    // Status filter
    if (statusFilter !== "all") {
      result = result.filter((chat) => chat.status === statusFilter);
    }

    // AI filter
    if (aiFilter === "enabled") {
      result = result.filter((chat) => chat.aiEnabled);
    } else if (aiFilter === "disabled") {
      result = result.filter((chat) => !chat.aiEnabled);
    }

    setFilteredChats(result);
  };

  return (
    <>
      <Header title="Chats" subtitle={`${filteredChats.length} conversations`} />

      <div className="p-6 space-y-4">
        {/* Filters */}
        <Card className="border-border/60">
          <CardContent className="p-4">
            <div className="grid grid-cols-2 md:flex md:flex-row gap-3">
              {/* Search */}
              <div className="relative col-span-2 md:flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search by name or phone..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10 h-10 w-full"
                />
              </div>

              {/* Status Filter */}
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-full md:w-40 h-10">
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="new">New</SelectItem>
                  <SelectItem value="initiated">Initiated</SelectItem>
                  <SelectItem value="converted">Converted</SelectItem>
                  <SelectItem value="temporary">Temporary</SelectItem>
                  <SelectItem value="invalid">Invalid</SelectItem>
                </SelectContent>
              </Select>

              {/* AI Filter */}
              <Select value={aiFilter} onValueChange={setAiFilter}>
                <SelectTrigger className="w-full md:w-40 h-10">
                  <Bot className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="AI Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="enabled">AI Enabled</SelectItem>
                  <SelectItem value="disabled">AI Disabled</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Chats List */}
        <div className="space-y-4">
          {isLoading ? (
            Array(5)
              .fill(0)
              .map((_, i) => (
                <Card key={i} className="border-border/60">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-4">
                      <Skeleton className="w-12 h-12 rounded-full" />
                      <div className="flex-1 space-y-2">
                        <Skeleton className="h-5 w-40" />
                        <Skeleton className="h-4 w-24" />
                      </div>
                      <Skeleton className="h-5 w-16" />
                    </div>
                  </CardContent>
                </Card>
              ))
          ) : filteredChats.length === 0 ? (
            <EmptyState
              icon={Search}
              title="No chats found"
              description="Try adjusting your filters or search terms"
              className="py-16 bg-card/50 rounded-2xl border border-dashed border-border/60"
            />
          ) : (
            filteredChats.map((chat) => (
              <Link key={chat.id} href={`/chats/${chat.id}`}>
                <Card className="border-border/60 hover:shadow-lg hover:border-primary/30 hover:bg-accent/5 transition-all cursor-pointer group active:scale-[0.995] relative overflow-hidden rounded-2xl">
                  {/* Activity Indicator */}
                  <div className="absolute left-0 top-0 bottom-0 w-1 bg-primary scale-y-0 group-hover:scale-y-100 transition-transform origin-top" />
                  
                  <CardContent className="p-4 sm:p-5">
                    <div className="flex items-start gap-4">
                      {/* Avatar */}
                      <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center shrink-0 shadow-sm group-hover:bg-primary/20 transition-colors">
                        <span className="text-lg font-bold text-primary">
                          {(chat.name || chat.phone)?.[0]?.toUpperCase() || "C"}
                        </span>
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0 flex flex-col gap-1.5">
                        {/* Top Row: Name & Time */}
                        <div className="flex items-center justify-between gap-2">
                          <h3 className="font-bold text-base text-foreground group-hover:text-primary transition-colors truncate">
                            {chat.name || "Unknown"}
                          </h3>
                          <div className="flex items-center gap-2 shrink-0">
                            <span className="text-xs text-muted-foreground font-medium flex items-center gap-1.5 bg-muted/50 px-2 py-1 rounded-full">
                              <Clock className="w-3 h-3" />
                              {formatTimeAgo(chat.updatedAt)}
                            </span>

                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8 rounded-full hover:bg-muted"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                  }}
                                  title="Actions"
                                >
                                  <MoreVertical className="w-4 h-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="w-48 p-1">
                                <DropdownMenuItem
                                  onSelect={(e) => {
                                    e.preventDefault();
                                    setRenameTarget(chat);
                                    setShowRename(true);
                                  }}
                                  className="cursor-pointer rounded-md"
                                >
                                  <Pencil className="w-4 h-4 mr-2" />
                                  <span className="font-medium">Rename Conversation</span>
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </div>

                        {/* Bottom Row: Phone/Source & Badges */}
                        <div className="flex items-center justify-between gap-3">
                          <div className="flex items-center gap-3 min-w-0">
                            <span className="text-sm text-muted-foreground/80 font-medium truncate flex items-center gap-1.5">
                              <Phone className="w-3.5 h-3.5 opacity-60" />
                              {chat.phone}
                            </span>
                          </div>
                          
                          <div className="flex items-center gap-2 shrink-0">
                             {/* Status Badge */}
                             <Badge
                              variant="secondary"
                              className={cn(
                                "text-[10px] px-2 h-6 uppercase font-bold tracking-wider rounded-full",
                                STATUS_COLORS[chat.status] || ""
                              )}
                            >
                              {chat.status}
                            </Badge>

                            {/* AI Badge */}
                            {chat.aiEnabled ? (
                              <Badge className="bg-success text-white border-0 px-2 h-6 gap-1.5 text-[10px] font-bold uppercase tracking-wider rounded-full shadow-sm shadow-success/20">
                                <Bot className="w-3 h-3" /> <span className="hidden sm:inline">AI Active</span>
                              </Badge>
                            ) : (
                              <Badge className="bg-warning text-warning-foreground border-0 px-2 h-6 gap-1.5 text-[10px] font-bold uppercase tracking-wider rounded-full">
                                <BotOff className="w-3 h-3" /> <span className="hidden sm:inline">AI Disabled</span>
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))
          )}
        </div>
      </div>

      <RenameChatDialog
        open={showRename}
        onOpenChange={setShowRename}
        initialName={renameTarget?.name ?? ""}
        onSubmit={async (name) => {
          if (!renameTarget) return;
          const res = await api.renameChat(renameTarget.id, name);
          setChats((prev) => prev.map((c) => (c.id === res.lead.id ? { ...c, ...res.lead } : c)));
        }}
      />
    </>
  );
}
